import {
  DatePicker,
  Icon,
  Input,
  Label,
  Modal,
  PaymentCollection
} from '@components';
import GroupDueDateConsent from '@features/group-management/components/v1/group-details-input/GroupDueDateConsent';
import useLeadGroupDateSettings from '@features/lead-management/hooks/useLeadGroupDateSettings';
import {
  FormDataType,
  MemberListType,
  PaymentCollectionComponentType,
  ValidationError
} from '@types';
import { formatISODate } from '@utils/date';

import theme from '../../../../../themes/colors';

interface LeadGroupDateSettingsProps {
  formData: FormDataType;
  groupId: string;
  startDate: string;
  setStartDate: (value: string) => void;
  onInputChange: (fieldName: string, value: any) => void;
  error?: ValidationError;
  setError?: (value: ValidationError) => void;
  isEditGroup: boolean;
  memberList?: MemberListType[];
  memberId?: string;
  groupDateClassName?: string;
  groupStartDateClassName?: string;
  groupEndDateClassName?: string;
}

const LeadGroupDateSettings = ({
  formData,
  groupId,
  startDate,
  setStartDate,
  onInputChange,
  error,
  setError,
  isEditGroup,
  memberList,
  memberId,
  groupDateClassName = '',
  groupStartDateClassName = '',
  groupEndDateClassName = ''
}: LeadGroupDateSettingsProps) => {
  const {
    isOrgLite,
    dueDateTooltipText,
    isDueDateDisabled,
    isStartDateDisabled,
    isEndDateDisabled,
    dueDateValue,
    normalizedPaymentType,
    paymentCollectionComponentType,
    isPaymentCollectionModalOpen,
    setIsPaymentCollectionModalOpen,
    isStartDateModalOpen,
    setIsStartDateModalOpen,
    isEndDateModalOpen,
    setIsEndDateModalOpen,
    showConsent,
    setShowConsent,
    onDueDateChange,
    onStartDateChange,
    onEndDateChange,
    onClearEndDate,
    handleConsentConfirm,
    endDateMinDate,
    endDateSelectedDate
  } = useLeadGroupDateSettings({
    formData,
    groupId,
    startDate,
    setStartDate,
    onInputChange,
    error,
    setError,
    isEditGroup,
    memberList,
    memberId
  });

  return (
    <>
      <div className={`w-full ${groupDateClassName}`}>
        <Label
          label="Fee Collection Day"
          className="!mb-1 !text-content"
          isRequired
        />
        <div>
          <Input
            readOnly
            leftIconName="calendar"
            value={dueDateValue}
            onClick={() => setIsPaymentCollectionModalOpen(true)}
            placeholder="Select payment collection day"
            disabled={isDueDateDisabled}
            inputStyle="!bg-theme"
          />
          {formData.activationDate && !formData.isCustomTerm && (
            <div className="mt-1 flex items-center gap-1 text-xs text-grey60">
              <Icon name="alert-circle" size="16" stroke={theme.subText} />
              <div>
                {`Payment ${isOrgLite ? 'request' : 'link'} will be generated ${
                  formData.isImmediatePayment ? '' : 'on'
                }`}{' '}
                {dueDateTooltipText}
              </div>
            </div>
          )}
        </div>
        {isPaymentCollectionModalOpen && (
          <Modal
            isOpen={isPaymentCollectionModalOpen}
            onCancel={() => setIsPaymentCollectionModalOpen(false)}
            isCloseIconRequired
          >
            <PaymentCollection
              type={
                isEditGroup
                  ? paymentCollectionComponentType
                  : PaymentCollectionComponentType.DEFAULT
              }
              isOpen={isPaymentCollectionModalOpen}
              confirmButtonLabel="Apply"
              confirmButtonStyle="text-primary"
              handleConfirm={onDueDateChange}
              cancelButtonLabel="Cancel"
              handleCancel={() => setIsPaymentCollectionModalOpen(false)}
              selectedDate={formData?.activationDate}
              selectedtype={normalizedPaymentType}
              selectedFrequency={formData?.intervalFrequency}
              {...(isEditGroup && { nextDueDate: formData?.activationDate })}
              isPaymentLinkSent={formData.isPaymentLinkSent}
              isImmediatePayment={formData.isImmediatePayment}
              isCustomTerm={formData.isCustomTerm}
              source="member"
            />
          </Modal>
        )}
      </div>

      <div className={`w-full ${groupStartDateClassName}`}>
        <Label
          label="Fee collection start date"
          className="!mb-1 !text-content"
          isRequired
        />
        <Input
          readOnly
          leftIconName="calendar"
          value={
            formData.startDate
              ? formatISODate({ date: formData.startDate })
              : ''
          }
          onClick={() => setIsStartDateModalOpen(true)}
          placeholder="Select payment start day"
          className={`${isStartDateDisabled ? 'text-grey' : ''}`}
          disabled={isStartDateDisabled}
          inputStyle="!bg-theme"
        />
        <div className="my-1 flex gap-1 text-xs text-grey60">
          <Icon
            name="alert-circle"
            stroke={theme.subText}
            size="16"
            className="mt-0.25"
          />
          Fee collection will begin from the start date.
        </div>

        {isStartDateModalOpen && (
          <Modal
            isOpen={isStartDateModalOpen}
            onCancel={() => setIsStartDateModalOpen(false)}
            isCloseIconRequired
          >
            <DatePicker
              confirmButtonLabel="Apply"
              confirmButtonStyle="text-primary"
              handleConfirm={onStartDateChange}
              cancelButtonLabel="Cancel"
              handleCancel={() => setIsStartDateModalOpen(false)}
              isMaxDateRequired={!!formData?.endDate}
              maxDate={
                formData?.endDate ? new Date(formData.endDate) : undefined
              }
              selectedDate={
                (formData?.startDate && new Date(formData?.startDate)) ||
                new Date()
              }
              calendarStyle="!bg-bgBase"
              title="Select Start Date"
            />
          </Modal>
        )}
      </div>

      <div className={`w-full ${groupEndDateClassName}`}>
        <Label
          label="Fee collection end date"
          className="!mb-1 !text-content"
        />
        <Input
          onClearIconAction={onClearEndDate}
          readOnly
          leftIconName="calendar"
          value={
            formData.endDate ? formatISODate({ date: formData.endDate }) : ''
          }
          onClick={() => setIsEndDateModalOpen(true)}
          placeholder="Select payment end day"
          error={!!error?.endDate}
          errorMessage={error?.endDate}
          className={`${isEndDateDisabled ? 'text-grey' : ''}`}
          disabled={isEndDateDisabled}
          inputStyle="!bg-theme"
        />
        <div
          className={`my-1 flex gap-1 text-xs text-grey60 ${
            error?.endDate ? 'mt-6' : ''
          }`}
        >
          <Icon
            name="alert-circle"
            stroke={theme.subText}
            size="16"
            className="mt-0.25 !min-w-4"
          />
          Group will be deactivated and payment links will not be sent after
          this date. All previously sent payment links will continue to work.
        </div>

        {isEndDateModalOpen && (
          <Modal
            isOpen={isEndDateModalOpen}
            onCancel={() => setIsEndDateModalOpen(false)}
            isCloseIconRequired
          >
            <DatePicker
              confirmButtonLabel="Apply"
              confirmButtonStyle="text-primary"
              handleConfirm={onEndDateChange}
              cancelButtonLabel="Cancel"
              handleCancel={() => setIsEndDateModalOpen(false)}
              minDate={endDateMinDate()}
              selectedDate={endDateSelectedDate()}
              calendarStyle="!bg-bgBase"
            />
          </Modal>
        )}
      </div>

      {showConsent && (
        <GroupDueDateConsent
          setShowConsent={setShowConsent}
          collectionType={paymentCollectionComponentType}
          handleConsentConfirm={handleConsentConfirm}
        />
      )}
    </>
  );
};

export default LeadGroupDateSettings;
